import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { formatCurrency } from '@hospora-bookings/shared-ui'
import { safeStorage } from '../lib/storage'

import { apiBaseUrl, sharedOrgSlug } from '../lib/runtime'
const API_URL = apiBaseUrl()

/**
 * The organisation this deployment serves, from the API's public GET /config (which reads Hospora):
 * its name, time zone, locale and currency, and whether JB & Sons' built-in venue rules are on.
 * Neutral defaults until it answers (or if it cannot): the app never invents an organisation.
 */
const cacheKey = (slug: string) => `bookings_org_regional:v2:${slug}:${API_URL}`

/** The time zone and locale seen last time, so pages that read them while they are being fetched use the right ones. */
function cachedRegional(): { timezone?: string; locale?: string } {
  try {
    const slug = sharedOrgSlug() || import.meta.env.VITE_HOSPORA_ORG
    return slug ? JSON.parse(safeStorage.getItem(cacheKey(slug)) || '{}') ?? {} : {}
  } catch {
    return {}
  }
}

export const useOrgStore = defineStore('org', () => {
  const cached = cachedRegional()
  const name = ref('')
  /** The Hospora organisation slug this deployment's API is bound to ('' until /config answers). */
  const slug = ref('')
  const timezone = ref<string | undefined>(cached.timezone)
  const locale = ref(cached.locale || 'en')
  const currency = ref('')
  const country = ref('')
  const contactUrl = ref<string | null>(null)
  /** JB & Sons' built-in venues: names and addresses are editable here, and the Manly cap etc. apply. */
  const builtInVenueRules = ref(false)
  /** This deployment can reach Orders, so the dashboard offers its pause button (Orders still decides per organisation). */
  const ordersPause = ref(false)
  /** Orders refused the pause for this organisation (it has no Orders): the button goes away. */
  const ordersPauseRefused = ref(false)
  /** The Orders pause is offered where an Orders API is configured for this build, or the API can reach Orders. */
  const ordersPauseAvailable = computed(() => (!!import.meta.env.VITE_ORDERS_API_URL || ordersPause.value) && !ordersPauseRefused.value)
  const loaded = ref(false)
  let inflight: Promise<void> | null = null

  function load(force = false): Promise<void> {
    if (inflight) return inflight
    if (loaded.value && !force) return Promise.resolve()
    inflight = (async () => {
      try {
        const res = await fetch(`${API_URL}/config`)
        if (!res.ok) return
        const body = await res.json()
        name.value = body?.org?.name ?? ''
        slug.value = typeof body?.org?.slug === 'string' ? body.org.slug : ''
        timezone.value = body?.org?.timezone || undefined
        locale.value = body?.org?.locale || 'en'
        currency.value = body?.org?.currency || ''
        country.value = body?.org?.country || ''
        contactUrl.value = body?.settings?.contactUrl ?? null
        builtInVenueRules.value = body?.settings?.builtInVenueRules === true
        ordersPause.value = body?.features?.ordersPause === true
        loaded.value = true
        try {
          if (slug.value) safeStorage.setItem(cacheKey(slug.value), JSON.stringify({ timezone: timezone.value, locale: locale.value }))
        } catch { /* storage unavailable */ }
      } catch {
        /* neutral defaults stay */
      } finally {
        inflight = null
      }
    })()
    return inflight
  }

  const hasOrg = computed(() => !!name.value)
  function money(value: number | null | undefined, exact = false): string {
    return formatCurrency(value, currency.value, locale.value, exact)
  }
  /** The organisation's currency symbol as its locale writes it ('$', '£', 'NZ$'...), for an input prefix; '' until the currency is known. */
  const currencySymbol = computed(() => {
    if (!currency.value) return ''
    try {
      return new Intl.NumberFormat(locale.value, { style: 'currency', currency: currency.value }).formatToParts(0).find((p) => p.type === 'currency')?.value ?? currency.value
    } catch {
      return currency.value
    }
  })
  return { name, slug, timezone, locale, currency, currencySymbol, country, contactUrl, builtInVenueRules, ordersPause, ordersPauseRefused, ordersPauseAvailable, loaded, hasOrg, load, money }
})
